// module imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// file imports
import axiosInstance from '@/lib/axios';

interface GetFeedbackArgs {
  params?: Record<string, string | number>;
}

const searchEventsAndPresentations = createAsyncThunk('adminFeedback/searchEventsAndPresentations', async ({ params = {} }: GetFeedbackArgs, { rejectWithValue }) => {
  try {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    const response = await axiosInstance.get(`/admin/feedbacks/search-events${query ? `?${query}` : ''}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving all events/presentations failed');
  }
});

const getAllFeedbacks = createAsyncThunk('adminFeedback/getAllFeedbacks', async ({ params = {} }: GetFeedbackArgs, { rejectWithValue }) => {
  try {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    const response = await axiosInstance.get(`/admin/feedbacks${query ? `?${query}` : ''}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving feedbacks failed');
  }
});

const getFeedback = createAsyncThunk('adminFeedback/getFeedback', async (feedbackId: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/admin/feedbacks/${feedbackId}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving single feedback failed');
  }
});

const updateFeedback = createAsyncThunk('adminFeedback/updateFeedback', async (params: { feedbackId: string; updatedData: any }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.patch(`/admin/feedbacks/${params.feedbackId}`, params.updatedData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Updating feedback failed');
  }
});

const deleteFeedback = createAsyncThunk('adminFeedback/deleteFeedback', async (feedbackId: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete(`/admin/feedbacks/${feedbackId}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Deleting feedback failed');
  }
});

const adminFeedbackSlice = createSlice({
  name: 'adminFeedback',
  initialState: {
    data: null as any,
    eventsList: [] as any[],
    loading: false,
    error: null as string | null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // searchEventsAndPresentations
      .addCase(searchEventsAndPresentations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchEventsAndPresentations.fulfilled, (state, action) => {
        state.loading = false;
        state.eventsList = action.payload;
      })
      .addCase(searchEventsAndPresentations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getAllFeedbacks
      .addCase(getAllFeedbacks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllFeedbacks.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getAllFeedbacks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getFeedback
      .addCase(getFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFeedback.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updateFeedback
      .addCase(updateFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFeedback.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(updateFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // deleteFeedback
      .addCase(deleteFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFeedback.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(deleteFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export { deleteFeedback, getAllFeedbacks, getFeedback, searchEventsAndPresentations, updateFeedback };
export default adminFeedbackSlice.reducer;
